/**
 * Theme product page interactivity
 * Handles gallery slider, variant selection, quantity, sticky mobile ATC, FAQ accordion, and carousel navigation.
 */

function initThemeProduct() {
  const containers = document.querySelectorAll('[data-aq-product]');
  if (!containers.length) return;

  containers.forEach(container => {
    if (container.dataset.aqInitialized) return;
    container.dataset.aqInitialized = 'true';

    // 1. Gallery Slider Interactivity
    const gallery = container.querySelector('[data-aq-gallery]');
    if (gallery) {
      const track = gallery.querySelector('[data-aq-gallery-track]');
      const slides = gallery.querySelectorAll('[data-aq-slide]');
      const thumbs = gallery.querySelectorAll('[data-aq-thumb]');
      const prevBtn = gallery.querySelector('[data-aq-gallery-prev]');
      const nextBtn = gallery.querySelector('[data-aq-gallery-next]');
      const mobileGalleryQuery = window.matchMedia('(max-width: 767px)');
      
      let currentIndex = 0;
      let galleryScrollFrame = null;

      const updateActiveMedia = (index) => {
        currentIndex = index;

        thumbs.forEach((thumb, idx) => {
          if (idx === currentIndex) {
            thumb.classList.add('is-active');
            thumb.setAttribute('aria-current', 'true');
            const scroller = thumb.parentElement;
            if (scroller) {
              const isVertical = window.getComputedStyle(scroller).flexDirection === 'column';
              if (isVertical) {
                const targetTop = thumb.offsetTop - scroller.offsetTop;
                scroller.scrollTo({
                  top: targetTop - (scroller.clientHeight / 2) + (thumb.clientHeight / 2),
                  behavior: 'smooth'
                });
              } else {
                const targetLeft = thumb.offsetLeft - scroller.offsetLeft;
                scroller.scrollTo({
                  left: targetLeft - (scroller.clientWidth / 2) + (thumb.clientWidth / 2),
                  behavior: 'smooth'
                });
              }
            }
          } else {
            thumb.classList.remove('is-active');
            thumb.removeAttribute('aria-current');
          }
        });
      };

      const getMobileSlideLeft = (index) => {
        if (!track || !slides.length) return 0;
        if (index === 0) return 0;
        if (index === slides.length - 1) return track.scrollWidth - track.clientWidth;

        const slide = slides[index];
        return slide.offsetLeft + (slide.offsetWidth / 2) - (track.clientWidth / 2);
      };

      const updateGallery = (index, behavior = 'smooth') => {
        if (index < 0) index = slides.length - 1;
        if (index >= slides.length) index = 0;
        updateActiveMedia(index);

        if (!track) return;
        if (mobileGalleryQuery.matches && slides.length > 1) {
          track.style.transform = 'none';
          track.scrollTo({ left: getMobileSlideLeft(index), behavior });
        } else {
          track.style.transform = `translateX(-${currentIndex * 100}%)`;
        }
      };

      if (track && slides.length > 1) {
        track.addEventListener('scroll', () => {
          if (!mobileGalleryQuery.matches || galleryScrollFrame) return;

          galleryScrollFrame = window.requestAnimationFrame(() => {
            galleryScrollFrame = null;
            const viewportCenter = track.scrollLeft + (track.clientWidth / 2);
            let nearestIndex = 0;
            let nearestDistance = Infinity;

            slides.forEach((slide, idx) => {
              const slideCenter = slide.offsetLeft + (slide.offsetWidth / 2);
              const distance = Math.abs(slideCenter - viewportCenter);
              if (distance < nearestDistance) {
                nearestDistance = distance;
                nearestIndex = idx;
              }
            });

            if (nearestIndex !== currentIndex) updateActiveMedia(nearestIndex);
          });
        }, { passive: true });

        mobileGalleryQuery.addEventListener('change', (event) => {
          if (event.matches) {
            track.style.transform = 'none';
            track.scrollTo({ left: getMobileSlideLeft(currentIndex), behavior: 'auto' });
          } else {
            track.scrollLeft = 0;
            track.style.transform = `translateX(-${currentIndex * 100}%)`;
          }
        });
      }

      thumbs.forEach((thumb, idx) => {
        thumb.addEventListener('click', () => {
          updateGallery(idx);
        });
      });

      if (prevBtn) {
        prevBtn.addEventListener('click', () => {
          updateGallery(currentIndex - 1);
        });
      }
      if (nextBtn) {
        nextBtn.addEventListener('click', () => {
          updateGallery(currentIndex + 1);
        });
      }

      gallery.updateToIndex = updateGallery;
      gallery.slides = Array.from(slides);
    }

    // Before / after image comparison
    container.querySelectorAll('[data-aq-before-after]').forEach(comparison => {
      const range = comparison.querySelector('[data-aq-before-after-range]');
      if (!range) return;
      const updateComparison = () => {
        comparison.style.setProperty('--comparison-position', `${range.value}%`);
      };
      range.addEventListener('input', updateComparison);
      updateComparison();
    });

    // 2. Variant Picker
    const variantPicker = container.querySelector('[data-aq-option-picker]');
    const variantInput = container.querySelector('[data-aq-variant-id]');

    let variantsData = null;
    const getVariantsData = () => {
      if (variantsData !== null) return variantsData;
      const variantJsonScript = container.querySelector('[data-aq-variants-json]');
      if (variantJsonScript) {
        try {
          variantsData = JSON.parse(variantJsonScript.textContent);
        } catch (e) {
          console.error('Failed to parse variant JSON data', e);
          variantsData = [];
        }
      } else {
        variantsData = [];
      }
      return variantsData;
    };

    initStickyPurchase(container, getVariantsData);

    if (variantPicker) {
      const optionInputs = variantPicker.querySelectorAll('[data-aq-option-value]');
      optionInputs.forEach(input => {
        input.addEventListener('change', () => {
          const optionGroup = input.closest('[data-aq-option-position]');
          if (optionGroup) {
            optionGroup.querySelectorAll('.aq-size').forEach(label => label.classList.remove('is-selected'));
            const selectedLabel = optionGroup.querySelector(`label[for="${CSS.escape(input.id)}"]`);
            if (selectedLabel) selectedLabel.classList.add('is-selected');
            const selectedValue = optionGroup.querySelector('[data-aq-option-selection]');
            if (selectedValue) selectedValue.textContent = input.value;
          }

          const selectedOptions = Array.from(
            variantPicker.querySelectorAll('[data-aq-option-position]')
          ).map(group => group.querySelector('[data-aq-option-value]:checked')?.value);
          const variant = getVariantsData().find(candidate =>
            candidate.options.every((option, index) => option === selectedOptions[index])
          );

          if (variant) {
            if (variantInput) variantInput.value = variant.id;
            const selectedUrl = new URL(window.location.href);
            selectedUrl.searchParams.set('variant', variant.id);
            window.history.replaceState({}, '', selectedUrl.toString());

            const priceNow = container.querySelector('[data-aq-price-now]');
            const priceWas = container.querySelector('[data-aq-price-was]');
            const priceOff = container.querySelector('[data-aq-price-off]');
            const unitPrice = container.querySelector('[data-aq-unit-price]');
            const quantityInput = container.querySelector('[data-aq-quantity], [data-aq-main-qty-input]');

            if (priceNow) {
              priceNow.textContent = variant.price_formatted;
            }

            if (variant.compare_at_price > variant.price) {
              if (priceWas) {
                priceWas.textContent = variant.compare_at_price_formatted;
                priceWas.style.display = '';
              }
              if (priceOff) {
                const offPercent = Math.round((1 - (variant.price / variant.compare_at_price)) * 100);
                const template = priceOff.dataset.offLabel || '% off';
                const pctStr = offPercent + '%';
                priceOff.textContent = template.includes('%') ? template.replace('%', pctStr) : (offPercent + ' ' + template);
                priceOff.style.display = '';
              }
            } else {
              if (priceWas) priceWas.style.display = 'none';
              if (priceOff) priceOff.style.display = 'none';
            }

            if (unitPrice) {
              if (variant.unit_price && variant.unit_price_reference_unit) {
                const referenceValue = variant.unit_price_reference_value !== 1
                  ? `${variant.unit_price_reference_value} `
                  : '';
                unitPrice.textContent = `${variant.unit_price_formatted} / ${referenceValue}${variant.unit_price_reference_unit}`;
                unitPrice.classList.remove('aq-hidden');
              } else {
                unitPrice.textContent = '';
                unitPrice.classList.add('aq-hidden');
              }
            }

            if (quantityInput && variant.quantity_rule) {
              const min = Number(variant.quantity_rule.min) || 1;
              const increment = Number(variant.quantity_rule.increment) || 1;
              quantityInput.min = String(min);
              quantityInput.step = String(increment);
              quantityInput.value = String(min);
              if (variant.quantity_rule.max) {
                quantityInput.max = String(variant.quantity_rule.max);
              } else {
                quantityInput.removeAttribute('max');
              }
            }

            if (variant.featured_image && gallery && gallery.updateToIndex) {
              const slideIdx = gallery.slides.findIndex(slide => {
                const img = slide.querySelector('img');
                return img && img.src.includes(variant.featured_image.split('?')[0]);
              });
              if (slideIdx !== -1) {
                gallery.updateToIndex(slideIdx);
              }
            }

            // Update Add to Cart and Buy Now button states
            const atcBtn = container.querySelector('[data-aq-add-to-cart-trigger], [data-aq-add-to-cart]');
            const buyNowBtn = container.querySelector('[data-aq-buy-now-trigger], [data-aq-buy-now]');
            if (atcBtn) {
              if (variant.available) {
                atcBtn.removeAttribute('disabled');
                atcBtn.textContent = atcBtn.dataset.labelAvailable || 'Add to cart';
              } else {
                atcBtn.setAttribute('disabled', 'disabled');
                atcBtn.textContent = atcBtn.dataset.labelSoldout || 'Sold out';
              }
            }
            if (buyNowBtn) {
              if (variant.available) {
                buyNowBtn.removeAttribute('disabled');
                buyNowBtn.textContent = buyNowBtn.dataset.labelAvailable || 'Buy Now';
              } else {
                buyNowBtn.setAttribute('disabled', 'disabled');
                buyNowBtn.textContent = buyNowBtn.dataset.labelSoldout || 'Sold out';
              }
            }
            syncStickyPurchaseVariant(container, variant);
            const pickupContainer = container.querySelector('[data-pickup-availability-content]');
            const sectionId = container.dataset.sectionId;
            if (pickupContainer && sectionId) {
              const url = new URL(window.location.href);
              url.searchParams.set('variant', variant.id);
              url.searchParams.set('section_id', sectionId);
              fetch(url.toString())
                .then(response => response.text())
                .then(html => {
                  const parsed = new DOMParser().parseFromString(html, 'text/html');
                  const updatedPickup = parsed.querySelector('[data-pickup-availability-content]');
                  if (updatedPickup) pickupContainer.innerHTML = updatedPickup.innerHTML;
                })
                .catch(() => {});
            }

            refreshProductAtcPills();
          } else {
            if (variantInput) variantInput.value = '';
            const atcBtn = container.querySelector('[data-aq-add-to-cart-trigger], [data-aq-add-to-cart]');
            if (atcBtn) {
              atcBtn.disabled = true;
              atcBtn.textContent = container.dataset.labelUnavailable || 'Unavailable';
            }
            syncStickyPurchaseVariant(container, null);
          }
        });
      });
    }

  });

  refreshProductAtcPills();
}

function setStickyPurchaseVisibility(stickyPurchase, shouldShow) {
  stickyPurchase.classList.toggle('is-visible', shouldShow);
  stickyPurchase.setAttribute('aria-hidden', shouldShow ? 'false' : 'true');
}

function setStickyPurchaseQuantity(container, requestedQuantity) {
  const stickyPurchase = container.querySelector('[data-aq-sticky-purchase]');
  const mainQuantityInput = container.querySelector('[data-aq-main-qty-input]');
  if (!stickyPurchase || !mainQuantityInput) return;

  const minimum = Math.max(1, Number(stickyPurchase.dataset.quantityMin) || 1);
  const increment = Math.max(1, Number(stickyPurchase.dataset.quantityIncrement) || 1);
  const maximum = Number(stickyPurchase.dataset.quantityMax) || 0;
  let quantity = Math.max(minimum, Number(requestedQuantity) || minimum);

  quantity = minimum + (Math.round((quantity - minimum) / increment) * increment);
  if (maximum > 0) quantity = Math.min(quantity, maximum);

  stickyPurchase.dataset.quantity = String(quantity);
  mainQuantityInput.value = String(quantity);

  const value = stickyPurchase.querySelector('[data-aq-sticky-qty-value]');
  const minus = stickyPurchase.querySelector('[data-aq-sticky-qty-minus]');
  const plus = stickyPurchase.querySelector('[data-aq-sticky-qty-plus]');
  if (value) value.textContent = String(quantity);
  if (minus) minus.disabled = quantity <= minimum;
  if (plus) plus.disabled = maximum > 0 && quantity >= maximum;
}

function syncStickyPurchaseVariant(container, variant) {
  const stickyPurchase = container.querySelector('[data-aq-sticky-purchase]');
  if (!stickyPurchase) return;

  const price = stickyPurchase.querySelector('[data-aq-sticky-price]');
  const compare = stickyPurchase.querySelector('[data-aq-sticky-compare]');
  const discount = stickyPurchase.querySelector('[data-aq-sticky-discount]');
  const image = stickyPurchase.querySelector('.aq-sticky-purchase__image img');
  const orderButton = stickyPurchase.querySelector('[data-aq-sticky-order]');

  if (!variant) {
    if (orderButton) {
      orderButton.disabled = true;
      orderButton.textContent = container.dataset.labelUnavailable || 'Unavailable';
    }
    return;
  }

  if (price) price.textContent = variant.price_formatted;

  if (variant.compare_at_price > variant.price) {
    const discountPercent = Math.round((1 - (variant.price / variant.compare_at_price)) * 100);
    if (compare) {
      compare.textContent = variant.compare_at_price_formatted;
      compare.hidden = false;
    }
    if (discount) {
      discount.textContent = `${discountPercent}% ${stickyPurchase.dataset.discountSuffix || ''}`.trim();
      discount.hidden = false;
    }
  } else {
    if (compare) compare.hidden = true;
    if (discount) discount.hidden = true;
  }

  if (image && variant.sticky_image) image.src = variant.sticky_image;

  if (orderButton) {
    orderButton.disabled = !variant.available;
    orderButton.textContent = variant.available
      ? (orderButton.dataset.labelAvailable || 'Buy now')
      : (orderButton.dataset.labelSoldout || 'Sold out');
  }

  const quantityRule = variant.quantity_rule || {};
  stickyPurchase.dataset.quantityMin = String(Number(quantityRule.min) || 1);
  stickyPurchase.dataset.quantityIncrement = String(Number(quantityRule.increment) || 1);
  stickyPurchase.dataset.quantityMax = quantityRule.max ? String(quantityRule.max) : '';
  setStickyPurchaseQuantity(container, Number(quantityRule.min) || 1);
}

function initStickyPurchase(container, getVariantsData) {
  const stickyPurchase = container.querySelector('[data-aq-sticky-purchase]');
  const purchaseActions = container.querySelector('.aq-buy-buttons-grid')
    || container.querySelector('.aq-buy-buttons-wrapper');
  if (!stickyPurchase || !purchaseActions) return;

  const variantInput = container.querySelector('[data-aq-variant-id]');
  const selectedVariant = getVariantsData().find((variant) => {
    return variantInput && String(variant.id) === String(variantInput.value);
  });
  syncStickyPurchaseVariant(container, selectedVariant || null);

  const updateVisibility = () => {
    const bounds = purchaseActions.getBoundingClientRect();
    const actionsAreVisible = bounds.bottom > 0 && bounds.top < window.innerHeight;
    setStickyPurchaseVisibility(stickyPurchase, !actionsAreVisible);
  };

  // Set the correct state immediately: the main buttons are normally below
  // the fold when a product page first opens, so the sticky bar starts visible.
  updateVisibility();

  if ('IntersectionObserver' in window) {
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        setStickyPurchaseVisibility(stickyPurchase, !entry.isIntersecting);
      });
    }, { threshold: 0 });
    observer.observe(purchaseActions);
  } else {
    window.addEventListener('scroll', updateVisibility, { passive: true });
    window.addEventListener('resize', updateVisibility);
  }

  const minus = stickyPurchase.querySelector('[data-aq-sticky-qty-minus]');
  const plus = stickyPurchase.querySelector('[data-aq-sticky-qty-plus]');

  if (minus) {
    minus.addEventListener('click', () => {
      const quantity = Number(stickyPurchase.dataset.quantity) || 1;
      const increment = Math.max(1, Number(stickyPurchase.dataset.quantityIncrement) || 1);
      setStickyPurchaseQuantity(container, quantity - increment);
    });
  }

  if (plus) {
    plus.addEventListener('click', () => {
      const quantity = Number(stickyPurchase.dataset.quantity) || 1;
      const increment = Math.max(1, Number(stickyPurchase.dataset.quantityIncrement) || 1);
      setStickyPurchaseQuantity(container, quantity + increment);
    });
  }
}

function formatMoney(cents, currencyCode) {
  if (typeof Shopify !== 'undefined' && typeof Shopify.formatMoney === 'function') {
    return Shopify.formatMoney(cents);
  }

  return new Intl.NumberFormat(document.documentElement.lang || 'en-IN', {
    style: 'currency',
    currency: currencyCode || 'INR'
  }).format(cents / 100);
}

function escapeHtml(value) {
  return String(value ?? '')
    .replaceAll('&', '&amp;')
    .replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;')
    .replaceAll('"', '&quot;')
    .replaceAll("'", '&#039;');
}

function updateCartCountBadges(cartData) {
  document.querySelectorAll('.site-header__cart-link').forEach((cartLink) => {
    let badge = cartLink.querySelector('.site-header__cart-count');

    if (!badge && cartData.item_count > 0) {
      badge = document.createElement('span');
      badge.className = 'site-header__cart-count';
      cartLink.appendChild(badge);
    }

    if (badge) {
      badge.textContent = String(cartData.item_count);
      badge.style.display = cartData.item_count > 0 ? 'inline-flex' : 'none';
    }

    cartLink.setAttribute('aria-label', `Cart, ${cartData.item_count} items`);
  });
}

function syncProductAtcPill(container, cartData) {
  const variantInput = container.querySelector('[data-aq-variant-id]');
  const variantId = variantInput ? String(variantInput.value) : '';
  if (!variantId) return;

  const quantity = cartData.items.reduce((total, item) => {
    return String(item.id) === variantId ? total + item.quantity : total;
  }, 0);

  container.querySelectorAll('[data-aq-atc-container]').forEach((atcContainer) => {
    const atcBtn = atcContainer.querySelector('[data-aq-add-to-cart-trigger], [data-aq-add-to-cart]');
    const qtyPill = atcContainer.querySelector('[data-aq-qty-pill]');
    const pillCount = atcContainer.querySelector('[data-aq-pill-count]');

    if (atcBtn) atcBtn.style.display = quantity > 0 ? 'none' : '';
    if (qtyPill) qtyPill.style.display = quantity > 0 ? 'flex' : 'none';
    if (pillCount) pillCount.textContent = String(quantity || 1);
  });
}

function syncProductAtcPills(cartData) {
  document.querySelectorAll('[data-aq-product]').forEach((container) => {
    syncProductAtcPill(container, cartData);
  });
}

function refreshProductAtcPills() {
  const rootPath = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) ? window.Shopify.routes.root : '/';

  return fetch(rootPath + 'cart.js')
    .then((response) => {
      if (!response.ok) throw new Error('Unable to load cart');
      return response.json();
    })
    .then((cartData) => {
      syncProductAtcPills(cartData);
      return cartData;
    })
    .catch(() => null);
}

function getCartAddItems(form, variantId) {
  const formData = form ? new FormData(form) : null;
  const requestedQuantity = Number(formData ? formData.get('quantity') : 1) || 1;
  const mainItem = { id: String(variantId), quantity: requestedQuantity };
  const sellingPlan = formData ? formData.get('selling_plan') : null;
  const properties = {};

  if (sellingPlan) mainItem.selling_plan = String(sellingPlan);

  if (formData) {
    formData.forEach((value, key) => {
      const propertyMatch = key.match(/^properties\[(.+)\]$/);
      if (propertyMatch && typeof value === 'string' && value !== '') {
        properties[propertyMatch[1]] = value;
      }
    });
  }

  if (Object.keys(properties).length) mainItem.properties = properties;

  const items = [mainItem];
  const siddhiAddon = form ? form.querySelector('[data-aq-siddhi-addon]') : null;
  const siddhiAddonVariantId = siddhiAddon ? siddhiAddon.dataset.aqSiddhiAddonVariantId : null;

  if (siddhiAddon && siddhiAddon.checked && siddhiAddonVariantId && String(siddhiAddonVariantId) !== String(variantId)) {
    items.push({ id: String(siddhiAddonVariantId), quantity: 1 });
  }

  return items;
}

function refreshCartDrawerUI() {
  const rootPath = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) ? window.Shopify.routes.root : '/';
  fetch(rootPath + 'cart.js')
    .then((response) => {
      if (!response.ok) throw new Error('Unable to load cart drawer');
      return response.json();
    })
    .then((cartData) => {
      syncProductAtcPills(cartData);

      updateCartCountBadges(cartData);
      document.querySelectorAll('[data-aq-drawer-count]').forEach((badge) => {
        badge.textContent = '(' + cartData.item_count + ')';
      });

      const subtotalElems = document.querySelectorAll('[data-aq-drawer-subtotal], [data-aq-drawer-subtotal-btn]');
      subtotalElems.forEach((el) => {
        el.textContent = formatMoney(cartData.total_price, cartData.currency);
      });

      const shippingContainer = document.querySelector('[data-aq-drawer-shipping]');
      if (shippingContainer) {
        const threshold = 69900;
        const remaining = threshold - cartData.total_price;
        if (remaining <= 0) {
          shippingContainer.innerHTML = '<div class="aq-shipping-msg aq-shipping-msg--unlocked">✨ <strong>Congratulations!</strong> You unlocked <strong>FREE Shipping!</strong></div><div class="aq-shipping-bar"><div class="aq-shipping-fill" style="width: 100%;"></div></div>';
        } else {
          const pct = Math.min(100, Math.max(0, Math.round((cartData.total_price / threshold) * 100)));
          shippingContainer.innerHTML = '<div class="aq-shipping-msg">Add <strong>' + escapeHtml(formatMoney(remaining, cartData.currency)) + '</strong> more to unlock <strong>FREE Shipping!</strong></div><div class="aq-shipping-bar"><div class="aq-shipping-fill" style="width: ' + pct + '%;"></div></div>';
        }
      }

      const bodyContainer = document.querySelector('[data-aq-drawer-body]');
      const footerContainer = document.querySelector('[data-aq-drawer-footer]');
      if (cartData.item_count > 0) {
        if (footerContainer) footerContainer.style.display = 'block';
        if (bodyContainer) {
          let itemsHtml = '<div class="aq-cart-drawer__items">';
          cartData.items.forEach((item) => {
            itemsHtml += `
              <div class="aq-drawer-item" data-item-key="${escapeHtml(item.key)}">
                <div class="aq-drawer-item__img">
                  ${item.image ? `<img src="${escapeHtml(item.image)}" alt="${escapeHtml(item.product_title)}" loading="lazy" width="70" height="70">` : ''}
                </div>
                <div class="aq-drawer-item__info">
                  <div class="aq-drawer-item__title-row">
                    <a href="${escapeHtml(item.url)}" class="aq-drawer-item__title">${escapeHtml(item.product_title)}</a>
                    <button type="button" class="aq-drawer-item__remove" data-aq-drawer-remove data-key="${escapeHtml(item.key)}" aria-label="Remove item">
                      <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polyline points="3 6 5 6 21 6"></polyline><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path></svg>
                    </button>
                  </div>
                  ${item.variant_title ? `<div class="aq-drawer-item__variant">${escapeHtml(item.variant_title)}</div>` : ''}
                  <div class="aq-drawer-item__bottom">
                    <div class="aq-drawer-item__price">${escapeHtml(formatMoney(item.final_line_price, cartData.currency))}</div>
                    <div class="aq-drawer-item__qty">
                      <button type="button" data-aq-drawer-qty-minus data-key="${escapeHtml(item.key)}" data-qty="${item.quantity}" data-min="${item.quantity_rule?.min || 1}" data-increment="${item.quantity_rule?.increment || 1}">−</button>
                      <span>${item.quantity}</span>
                      <button type="button" data-aq-drawer-qty-plus data-key="${escapeHtml(item.key)}" data-qty="${item.quantity}" data-increment="${item.quantity_rule?.increment || 1}">+</button>
                    </div>
                  </div>
                </div>
              </div>
            `;
          });
          itemsHtml += '</div>';
          bodyContainer.innerHTML = itemsHtml;
        }
      } else {
        if (footerContainer) footerContainer.style.display = 'none';
        if (bodyContainer) {
          bodyContainer.innerHTML = `
            <div class="aq-cart-drawer__empty">
              <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#b0a599" stroke-width="1.5"><circle cx="9" cy="21" r="1"/><circle cx="20" cy="21" r="1"/><path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6"/></svg>
              <p>Your cart is empty</p>
              <a href="/collections/all" class="aq-drawer-shop-btn">Explore Collection</a>
            </div>
          `;
        }
      }
    })
    .catch(() => {});
}

let cartDrawerTrigger = null;

function openCartDrawerDesktop(trigger) {
  if (window.innerWidth >= 990) {
    const drawer = document.getElementById('AqCartDrawer');
    const overlay = document.getElementById('AqCartDrawerOverlay');
    refreshCartDrawerUI();
    if (drawer) drawer.classList.add('is-active');
    if (drawer) drawer.setAttribute('aria-hidden', 'false');
    if (overlay) overlay.classList.add('is-active');
    document.body.style.overflow = 'hidden';
    cartDrawerTrigger = trigger || document.activeElement;
    const closeButton = drawer ? drawer.querySelector('[data-aq-drawer-close]') : null;
    if (closeButton) closeButton.focus();
  }
}

function closeCartDrawer() {
  const drawer = document.getElementById('AqCartDrawer');
  const overlay = document.getElementById('AqCartDrawerOverlay');
  if (drawer) drawer.classList.remove('is-active');
  if (drawer) drawer.setAttribute('aria-hidden', 'true');
  if (overlay) overlay.classList.remove('is-active');
  document.body.style.overflow = '';
  if (cartDrawerTrigger && typeof cartDrawerTrigger.focus === 'function') cartDrawerTrigger.focus();
  cartDrawerTrigger = null;
}

// Global Event Delegation for Product Buy Box Pill Buttons (ATC, Buy Now, Quantity Pill), Accordion & Carousels
document.addEventListener('click', (e) => {
  // 1. Add to Cart Click Handler
  const atcBtn = e.target.closest('[data-aq-add-to-cart-trigger], [data-aq-add-to-cart]');
  if (atcBtn) {
    e.preventDefault();

    const form = atcBtn.closest('form') || document.querySelector('form[action*="/cart/add"]');
    const variantInput = form ? (form.querySelector('input[name="id"]') || form.querySelector('[data-aq-variant-id]')) : document.querySelector('input[name="id"]');
    const variantId = variantInput ? variantInput.value : null;

    if (!variantId) {
      console.warn('Velmukha: Variant ID not found for Add to Cart');
      return;
    }

    const containerPill = atcBtn.closest('[data-aq-atc-container]') || atcBtn.parentElement;
    const qtyPill = containerPill ? containerPill.querySelector('[data-aq-qty-pill]') : null;
    const pillCount = containerPill ? containerPill.querySelector('[data-aq-pill-count]') : null;

    atcBtn.disabled = true;
    const originalText = atcBtn.textContent;
    atcBtn.textContent = 'Adding...';

    const cartItems = getCartAddItems(form, variantId);

    const rootPath = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) ? window.Shopify.routes.root : '/';

    fetch(rootPath + 'cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ items: cartItems })
    })
    .then((res) => {
      if (!res.ok) throw new Error('Add to cart failed');
      return res.json();
    })
    .then(() => {
      if (pillCount) pillCount.textContent = '1';

      // Hide Add to cart button, show Quantity Pill
      atcBtn.style.display = 'none';
      atcBtn.disabled = false;
      atcBtn.textContent = originalText;
      if (qtyPill) qtyPill.style.display = 'flex';

      // Update header cart count
      fetch(rootPath + 'cart.js')
        .then((r) => r.json())
        .then((cartData) => {
          updateCartCountBadges(cartData);
          syncProductAtcPills(cartData);
        })
        .catch(() => {});

      // Open Desktop Cart Sidebar Drawer
      if (window.innerWidth >= 990) {
        openCartDrawerDesktop(atcBtn);
      }
    })
    .catch((err) => {
      console.error('Velmukha Add to Cart Error:', err);
      atcBtn.disabled = false;
      atcBtn.textContent = originalText;
    });
    return;
  }

  // Header Cart Icon Click Handler
  const cartIcon = e.target.closest('.site-header__cart-link');
  if (cartIcon && window.innerWidth >= 990) {
    e.preventDefault();
    openCartDrawerDesktop(cartIcon);
    return;
  }

  // Drawer Close Handler
  const drawerClose = e.target.closest('[data-aq-drawer-close]');
  if (drawerClose) {
    e.preventDefault();
    closeCartDrawer();
    return;
  }

  // Drawer Quantity Plus
  const drawerPlus = e.target.closest('[data-aq-drawer-qty-plus]');
  if (drawerPlus) {
    e.preventDefault();
    const key = drawerPlus.dataset.key;
    const currentQty = parseInt(drawerPlus.dataset.qty || '1', 10);
    const increment = Math.max(1, parseInt(drawerPlus.dataset.increment || '1', 10));
    const rootPath = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) ? window.Shopify.routes.root : '/';

    drawerPlus.disabled = true;
    const formData = new FormData();
    formData.append('id', key);
    formData.append('quantity', String(currentQty + increment));

    fetch(rootPath + 'cart/change.js', { method: 'POST', body: formData })
      .then((response) => {
        if (!response.ok) throw new Error('Unable to increase cart quantity');
        return refreshCartDrawerUI();
      })
      .catch(() => {})
      .finally(() => { drawerPlus.disabled = false; });
    return;
  }

  // Drawer Quantity Minus
  const drawerMinus = e.target.closest('[data-aq-drawer-qty-minus]');
  if (drawerMinus) {
    e.preventDefault();
    const key = drawerMinus.dataset.key;
    const currentQty = parseInt(drawerMinus.dataset.qty || '1', 10);
    const minimum = Math.max(1, parseInt(drawerMinus.dataset.min || '1', 10));
    const increment = Math.max(1, parseInt(drawerMinus.dataset.increment || '1', 10));
    const decrementedValue = currentQty - increment;
    const targetQuantity = decrementedValue < minimum ? 0 : decrementedValue;
    const rootPath = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) ? window.Shopify.routes.root : '/';

    drawerMinus.disabled = true;
    const formData = new FormData();
    formData.append('id', key);
    formData.append('quantity', String(targetQuantity));

    fetch(rootPath + 'cart/change.js', { method: 'POST', body: formData })
      .then((response) => {
        if (!response.ok) throw new Error('Unable to decrease cart quantity');
        return refreshCartDrawerUI();
      })
      .catch(() => {})
      .finally(() => { drawerMinus.disabled = false; });
    return;
  }

  // Drawer Remove Item
  const drawerRemove = e.target.closest('[data-aq-drawer-remove]');
  if (drawerRemove) {
    e.preventDefault();
    const key = drawerRemove.dataset.key;
    const rootPath = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) ? window.Shopify.routes.root : '/';

    const formData = new FormData();
    formData.append('id', key);
    formData.append('quantity', '0');

    fetch(rootPath + 'cart/change.js', { method: 'POST', body: formData })
      .then((response) => {
        if (!response.ok) throw new Error('Unable to remove cart item');
        return refreshCartDrawerUI();
      })
      .catch(() => {});
    return;
  }

  // 2. Buy Now Click Handler
  const buyNowBtn = e.target.closest('[data-aq-buy-now-trigger], [data-aq-buy-now]');
  if (buyNowBtn) {
    e.preventDefault();

    const productContainer = buyNowBtn.closest('[data-aq-product]');
    const form = buyNowBtn.closest('form') || (productContainer && productContainer.querySelector('form[action*="/cart/add"]')) || document.querySelector('form[action*="/cart/add"]');
    const variantInput = form ? (form.querySelector('input[name="id"]') || form.querySelector('[data-aq-variant-id]')) : document.querySelector('input[name="id"]');
    const variantId = variantInput ? variantInput.value : null;

    if (!variantId) {
      console.warn('Velmukha: Variant ID not found for Buy Now');
      return;
    }

    buyNowBtn.disabled = true;
    const originalText = buyNowBtn.textContent;
    buyNowBtn.textContent = 'Redirecting...';

    const rootPath = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) ? window.Shopify.routes.root : '/';
    const cartItems = getCartAddItems(form, variantId);

    fetch(rootPath + 'cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ items: cartItems })
    })
    .then((response) => {
      if (!response.ok) throw new Error('Buy now failed');
      window.location.href = rootPath + 'checkout';
    })
    .catch((error) => {
      console.error('Velmukha Buy Now Error:', error);
      buyNowBtn.disabled = false;
      buyNowBtn.textContent = originalText;
    });
    return;
  }

  // 3. Plus (+1) Quantity Pill Button
  const plusBtn = e.target.closest('[data-aq-pill-plus]');
  if (plusBtn) {
    e.preventDefault();
    const containerPill = plusBtn.closest('[data-aq-atc-container]') || plusBtn.parentElement;
    const form = plusBtn.closest('form') || document.querySelector('form[action*="/cart/add"]');
    const variantInput = form ? (form.querySelector('input[name="id"]') || form.querySelector('[data-aq-variant-id]')) : document.querySelector('input[name="id"]');
    const variantId = variantInput ? variantInput.value : null;
    const pillCount = containerPill ? containerPill.querySelector('[data-aq-pill-count]') : null;
    const quantityInput = form ? form.querySelector('[name="quantity"]') : null;

    if (!variantId) return;

    plusBtn.disabled = true;
    const rootPath = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) ? window.Shopify.routes.root : '/';
    const formData = new FormData();
    formData.append('id', String(variantId));
    const increment = Math.max(1, Number(quantityInput ? quantityInput.step : 1) || 1);
    formData.append('quantity', String(increment));

    fetch(rootPath + 'cart/add.js', {
      method: 'POST',
      body: formData
    })
    .then((response) => {
      if (!response.ok) throw new Error('Unable to increase quantity');
      return response.json();
    })
    .then(() => {
      plusBtn.disabled = false;
      let currentVal = parseInt(pillCount ? pillCount.textContent : '1', 10) || 1;
      currentVal += increment;
      if (pillCount) pillCount.textContent = String(currentVal);

      fetch(rootPath + 'cart.js')
        .then((r) => r.json())
        .then((cartData) => {
          updateCartCountBadges(cartData);
          syncProductAtcPills(cartData);
        })
        .catch(() => {});
    })
    .catch(() => { plusBtn.disabled = false; });
    return;
  }

  // 4. Minus (-1) Quantity Pill Button
  const minusBtn = e.target.closest('[data-aq-pill-minus]');
  if (minusBtn) {
    e.preventDefault();
    const containerPill = minusBtn.closest('[data-aq-atc-container]') || minusBtn.parentElement;
    const form = minusBtn.closest('form') || document.querySelector('form[action*="/cart/add"]');
    const variantInput = form ? (form.querySelector('input[name="id"]') || form.querySelector('[data-aq-variant-id]')) : document.querySelector('input[name="id"]');
    const variantId = variantInput ? variantInput.value : null;
    const pillCount = containerPill ? containerPill.querySelector('[data-aq-pill-count]') : null;
    const atcBtn = containerPill ? containerPill.querySelector('[data-aq-add-to-cart-trigger], [data-aq-add-to-cart]') : null;
    const qtyPill = containerPill ? containerPill.querySelector('[data-aq-qty-pill]') : null;
    const quantityInput = form ? form.querySelector('[name="quantity"]') : null;

    if (!variantId) return;

    minusBtn.disabled = true;
    let currentVal = parseInt(pillCount ? pillCount.textContent : '1', 10) || 1;
    const minimum = Math.max(1, Number(quantityInput ? quantityInput.min : 1) || 1);
    const increment = Math.max(1, Number(quantityInput ? quantityInput.step : 1) || 1);
    const decrementedValue = currentVal - increment;
    const targetVal = decrementedValue < minimum ? 0 : decrementedValue;

    const rootPath = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) ? window.Shopify.routes.root : '/';
    const formData = new FormData();
    formData.append('id', String(variantId));
    formData.append('quantity', String(targetVal));

    fetch(rootPath + 'cart/change.js', {
      method: 'POST',
      body: formData
    })
    .then((response) => {
      if (!response.ok) throw new Error('Unable to decrease quantity');
      return response.json();
    })
    .then(() => {
      minusBtn.disabled = false;
      if (targetVal <= 0) {
        if (qtyPill) qtyPill.style.display = 'none';
        if (atcBtn) atcBtn.style.display = 'inline-flex';
      } else {
        if (pillCount) pillCount.textContent = String(targetVal);
      }

      fetch(rootPath + 'cart.js')
        .then((r) => r.json())
        .then((cartData) => {
          updateCartCountBadges(cartData);
          syncProductAtcPills(cartData);
        })
        .catch(() => {});
    })
    .catch(() => { minusBtn.disabled = false; });
    return;
  }

  const copyOfferButton = e.target.closest('[data-aq-copy-offer]');
  if (copyOfferButton) {
    const code = copyOfferButton.dataset.code;
    if (code && navigator.clipboard) {
      navigator.clipboard.writeText(code).then(() => {
        const originalLabel = copyOfferButton.textContent;
        copyOfferButton.textContent = 'Copied';
        window.setTimeout(() => { copyOfferButton.textContent = originalLabel; }, 1600);
      });
    }
    return;
  }

  // FAQ Accordion Click Handler
  const q = e.target.closest('.accordion-q');
  if (q) {
    if (e.faqHandled) return;
    e.faqHandled = true;
    const item = q.closest('.accordion-item');
    if (item) {
      const wasOpen = item.classList.contains('open');
      const accordion = item.closest('.accordion');
      if (accordion) {
        accordion.querySelectorAll('.accordion-item').forEach(i => i.classList.remove('open'));
      }
      if (!wasOpen) {
        item.classList.add('open');
      }
    }
    return;
  }

  // Carousel Navigation Click Handler
  const carBtn = e.target.closest('.car-nav');
  if (carBtn) {
    const wrap = carBtn.closest('.carousel-wrap');
    if (wrap) {
      const track = wrap.querySelector('.carousel');
      if (track) {
        const card = track.querySelector('.product-card');
        const step = card ? card.offsetWidth + 18 : 250;
        if (carBtn.classList.contains('car-prev')) {
          track.scrollBy({ left: -step * 2, behavior: 'smooth' });
        } else if (carBtn.classList.contains('car-next')) {
          track.scrollBy({ left: step * 2, behavior: 'smooth' });
        }
      }
    }
    return;
  }
});

document.addEventListener('keydown', (event) => {
  if (event.key === 'Escape') {
    const drawer = document.getElementById('AqCartDrawer');
    if (drawer && drawer.classList.contains('is-active')) closeCartDrawer();
  }
});

// Run immediately or on DomContentLoaded
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initThemeProduct);
} else {
  initThemeProduct();
}

// Bind to editor events for live PDP changes inside the Shopify Customizer
document.addEventListener('shopify:section:load', (e) => {
  if (e.target.querySelector('[data-aq-product]')) {
    initThemeProduct();
  }
});
